import { useState, useEffect } from 'react';
import {
  Building2,
  Users,
  Cpu,
  DollarSign,
  BarChart3,
  Server,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';
import { adminApi } from '../../services/api';

interface AdminStats {
  totalTenants: number;
  activeTenants: number;
  totalUsers: number;
  totalDevices: number;
  activeDevices: number;
  totalRevenue: number;
  monthlyRevenue: number;
  subscriptionsByPlan: Record<string, number>;
  subscriptionsByStatus: Record<string, number>;
  recentTenants: Array<{
    id: string;
    name: string;
    plan: string;
    createdAt: string;
  }>;
}

interface SystemHealth {
  status: string;
  database: { status: string; latency: number };
  redis: { status: string; latency: number };
  tcp: { status: string; connections: number };
  websocket: { status: string; connections: number };
}

export default function AdminDashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, healthRes] = await Promise.all([
        adminApi.getStats(),
        adminApi.getHealth(),
      ]);
      setStats(statsRes.data);
      setHealth(healthRes.data);
    } catch (error) {
      console.error('Erreur chargement données admin:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500" />
      </div>
    );
  }

  const statCards = [
    {
      name: 'Tenants Actifs',
      value: stats?.activeTenants || 0,
      total: stats?.totalTenants || 0,
      icon: Building2,
      color: 'cyan',
    },
    {
      name: 'Utilisateurs',
      value: stats?.totalUsers || 0,
      icon: Users,
      color: 'purple',
    },
    {
      name: 'Appareils Actifs',
      value: stats?.activeDevices || 0,
      total: stats?.totalDevices || 0,
      icon: Cpu,
      color: 'green',
    },
    {
      name: 'Revenu Mensuel',
      value: `${(stats?.monthlyRevenue || 0).toLocaleString('fr-FR')} €`,
      icon: DollarSign,
      color: 'yellow',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Super Admin</h1>
          <p className="text-gray-500">Vue d'ensemble de la plateforme</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div key={stat.name} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                {stat.total && (
                  <p className="text-gray-400 text-xs">sur {stat.total} total</p>
                )}
              </div>
              <div className={`p-3 rounded-lg bg-${stat.color}-500/10`}>
                <stat.icon className={`h-6 w-6 text-${stat.color}-500`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* System Health */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Server className="h-5 w-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Santé du Système</h2>
          <span
            className={`ml-auto px-2 py-0.5 rounded text-xs font-medium ${
              health?.status === 'healthy'
                ? 'bg-green-500/20 text-green-400'
                : 'bg-red-500/20 text-red-400'
            }`}
          >
            {health?.status === 'healthy' ? 'Opérationnel' : 'Dégradé'}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {health && (
            <>
              <HealthItem
                name="Base de données"
                status={health.database?.status || 'unknown'}
                detail={`${health.database?.latency || 0}ms`}
              />
              <HealthItem
                name="Redis"
                status={health.redis?.status || 'unknown'}
                detail={`${health.redis?.latency || 0}ms`}
              />
              <HealthItem
                name="Serveur TCP"
                status={health.tcp?.status || 'unknown'}
                detail={`${health.tcp?.connections || 0} conn.`}
              />
              <HealthItem
                name="WebSocket"
                status={health.websocket?.status || 'unknown'}
                detail={`${health.websocket?.connections || 0} conn.`}
              />
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscriptions by Plan */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Abonnements par Plan</h2>
          </div>
          <div className="space-y-3">
            {stats?.subscriptionsByPlan &&
              Object.entries(stats.subscriptionsByPlan).map(([plan, count]) => (
                <div key={plan} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={`w-3 h-3 rounded-full ${
                        plan === 'enterprise'
                          ? 'bg-purple-500'
                          : plan === 'pro'
                          ? 'bg-cyan-500'
                          : plan === 'basic'
                          ? 'bg-blue-500'
                          : 'bg-gray-400'
                      }`}
                    />
                    <span className="text-gray-600 capitalize">{plan}</span>
                  </div>
                  <span className="text-gray-900 font-medium">{count}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Recent Tenants */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Nouveaux Tenants</h2>
          </div>
          <div className="space-y-3">
            {stats?.recentTenants?.map((tenant) => (
              <div
                key={tenant.id}
                className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0"
              >
                <div>
                  <p className="text-gray-900 font-medium">{tenant.name}</p>
                  <p className="text-gray-400 text-xs">
                    {new Date(tenant.createdAt).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                    tenant.plan === 'enterprise'
                      ? 'bg-purple-500/20 text-purple-400'
                      : tenant.plan === 'pro'
                      ? 'bg-cyan-500/20 text-cyan-400'
                      : tenant.plan === 'basic'
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tenant.plan}
                </span>
              </div>
            ))}
            {(!stats?.recentTenants || stats.recentTenants.length === 0) && (
              <p className="text-gray-400 text-center py-4">Aucun tenant récent</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HealthItem({
  name,
  status,
  detail,
}: {
  name: string;
  status: string;
  detail: string;
}) {
  const isHealthy = status === 'connected' || status === 'running' || status === 'healthy';
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex items-center gap-2">
        {isHealthy ? (
          <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-red-500" />
        )}
        <span className="text-gray-600 text-sm">{name}</span>
      </div>
      <p className="text-gray-900 font-medium mt-1">{detail}</p>
    </div>
  );
}
